import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import ReactDOM from 'react-dom';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, SingleDatePicker } from '../../components';
import { computePortalPosition } from '../../utils/portalPosition';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { talentProjectsApi, talentSpecializationsApi } from '../../services/apiService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddProject'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// KPI Form Interface
// ============================================================

interface KPIFormData {
  id: string;
  /** Server id of an existing TalentKPI — set only for KPIs loaded in edit mode. */
  kpiId?: number;
  name: string;
  weight: string;
  description: string;
  kpiType: string;
  polarity: string;
  unit: string;
  frequency: string;
  formula: string;
  targetValue: string;
  dataSource: string;
  goalWeight: string;
  startDate: string;
  isGeneral: boolean;
  isExpanded: boolean;
}

interface DaySchedule {
  day: string;
  labelAr: string;
  labelEn: string;
  selected: boolean;
  startTime: string;
  endTime: string;
}

interface CustomDateEntry {
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
}

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const ClockIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// ============================================================
// Single Date Picker Component (portal-based calendar)
// ============================================================

const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const AR_DAYS_SHORT = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const EN_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
}

// Legacy in-file picker — superseded by the shared components/SingleDatePicker. Unused.
const _LegacySingleDatePicker = ({ value, onChange, minDate, maxDate, placeholder }: SingleDatePickerProps) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);

  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    const d = value ? new Date(value) : (minDate ? new Date(minDate) : new Date());
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setLayout({ x, y, width: w, height: h });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleSelect = (dateStr: string) => {
    onChange(dateStr);
    setIsOpen(false);
  };

  const months = isRTL ? AR_MONTHS : EN_MONTHS;
  const dayHeaders = isRTL ? AR_DAYS_SHORT : EN_DAYS_SHORT;
  const cells = buildMonthCells(viewYear, viewMonth);

  const displayValue = value
    ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })()
    : '';

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'fixed', top: layout.y + layout.height + 4, left: Math.max(8, layout.x + layout.width - 320), zIndex: 99999 }}>
        <View style={[dpStyles.panel, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={dpStyles.monthNav}>
            <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}>
              <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
            </TouchableOpacity>
            <Text style={[dpStyles.monthLabel, { color: colors.textPrimary }]}>{months[viewMonth]} {viewYear}</Text>
            <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}>
              <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
            </TouchableOpacity>
          </View>
          <View style={dpStyles.dayHeaderRow}>
            {dayHeaders.map(h => (
              <Text key={h} style={[dpStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>
            ))}
          </View>
          <View style={dpStyles.grid}>
            {cells.map((cell, idx) => {
              const isSelected = cell.dateStr === value;
              const isToday = cell.dateStr === todayStr;
              const isDisabled = !cell.isCurrentMonth || (minDate ? cell.dateStr < minDate : false) || (maxDate ? cell.dateStr > maxDate : false);
              return (
                <TouchableOpacity
                  key={idx}
                  style={[dpStyles.cell, isSelected && { backgroundColor: '#a9211f', borderRadius: 20 }, isToday && !isSelected && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 20 }]}
                  onPress={() => !isDisabled && handleSelect(cell.dateStr)}
                  disabled={isDisabled}
                >
                  <Text style={[dpStyles.cellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{cell.day}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity onPress={handleOpen} style={[dpStyles.button, { backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', borderColor: colors.border }]}>
        <CalendarIcon size={16} color={colors.textMuted} />
        <Text style={[dpStyles.buttonText, { color: value ? colors.textPrimary : colors.textMuted }]}>
          {displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: 320, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
});

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
  };

  const handleOpen = () => {
    if (disabled) return;
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    // Reposition to follow the trigger on scroll/resize instead of closing.
    const reposition = () => {
      buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
        setButtonLayout({ x, y, width, height });
      });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const dropPos = computePortalPosition(buttonLayout, buttonLayout.width || 200, 240);
  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: dropPos.top,
          left: dropPos.left,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: dropdownTheme.bg,
          border: `1px solid ${dropdownTheme.border}`,
          borderRadius: 12,
          paddingTop: 8,
          paddingBottom: 8,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
        }}
      >
        {options.map((option) => (
          <div
            key={option.value}
            style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', textAlign: 'right', direction: 'rtl' }}
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
          >
            <Text style={[styles.dropdownItemText, { color: dropdownTheme.text, width: '100%' }]}>{option.label}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.dropdownButton, { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border }, disabled && styles.dropdownDisabled]}
        onPress={handleOpen}
        disabled={disabled}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[styles.dropdownButtonText, { color: dropdownTheme.text }, !selectedOption && { color: dropdownTheme.placeholder }]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Multi-Select Tags Component
// ============================================================

interface MultiSelectProps {
  values: string[];
  options: string[];
  placeholder: string;
  onChange: (values: string[]) => void;
}

const MultiSelectTags = ({ values, options, placeholder, onChange }: MultiSelectProps) => {
  const { colors, isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);

  const bg = colors.card;
  const border = colors.border;
  const text = colors.textPrimary;

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  const toggleOption = (opt: string) => {
    if (values.includes(opt)) {
      onChange(values.filter(v => v !== opt));
    } else {
      onChange([...values, opt]);
    }
  };

  const removeTag = (tag: string) => {
    onChange(values.filter(v => v !== tag));
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
        onClick={() => setIsOpen(false)}
      />
      <div
        style={{
          position: 'fixed',
          top: buttonLayout.y + buttonLayout.height + 4,
          left: buttonLayout.x,
          width: buttonLayout.width,
          zIndex: 99999,
          backgroundColor: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          paddingTop: 8,
          paddingBottom: 8,
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
          maxHeight: 200,
          overflowY: 'auto' as const,
        }}
      >
        {options.filter(o => !values.includes(o)).map((option) => (
          <div
            key={option}
            style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', textAlign: 'right', direction: 'rtl' }}
            onClick={() => { toggleOption(option); setIsOpen(false); }}
          >
            <Text style={[styles.dropdownItemText, { color: text, width: '100%' }]}>{option}</Text>
          </div>
        ))}
      </div>
    </>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[styles.multiSelectButton, { backgroundColor: bg, borderColor: border }]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color="#6B7280" />
        <View style={styles.tagsWrap}>
          {values.length === 0 && (
            <Text style={[styles.dropdownButtonText, { color: '#9CA3AF' }]}>{placeholder}</Text>
          )}
          {values.map((tag) => (
            <TouchableOpacity key={tag} style={styles.tag} onPress={() => removeTag(tag)}>
              <Text style={styles.tagX}>&times;</Text>
              <Text style={styles.tagText}>{tag}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Color options
// ============================================================
const COLOR_OPTIONS = ['#2ca02c', '#3a5aa3', '#a9211f', '#a6813f', '#9467bd', '#ff7f0e', '#1f77b4', '#e377c2'];

// ============================================================
// Mock Data
// ============================================================
const MOCK_TEACHERS = ['سالم برهان', 'سالم عبد الرحمن محمود', 'احمد السالمي'];
const MOCK_STUDENTS = [
  'سالم برهان', 'سالم عبد الرحمن محمود', 'مصطفى عبدالله',
  'احمد الراشد', 'أمل برهان', 'خالد المنصوري',
  'فاطمة الشامسي', 'نورة المزروعي',
];

const LOCATION_OPTIONS: DropdownOption[] = [
  { value: '1', label: 'مدرسة الخط والزخرفة' },
  { value: '2', label: 'ثانوية الفجيرة الخاصة' },
  { value: '3', label: 'مدرسة الفنون الجميلة' },
];

// ============================================================
// Main Component
// ============================================================

export default function AddProjectScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const scrollViewRef = useRef<ScrollView>(null);
  const { isMobile, containerPadding } = useResponsive();

  const specializationId = route.params?.specializationId;
  const projectId = route.params?.projectId;
  const isEditMode = !!projectId;

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: isDark ? colors.cardBorder : '#E2E8F0',
    sectionTitle: colors.textPrimary,
    label: '#414042',
    input: colors.card,
    inputBorder: isDark ? colors.border : '#E2E8F0',
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : '#F1F5F9',
    cancelButton: colors.card,
    cancelButtonBorder: isDark ? colors.border : '#CBD5E1',
    cancelButtonText: colors.textMuted,
  };

  // Form state — empty defaults; edit mode fills via useEffect
  const [name, setName] = useState('');
  const [weight, setWeight] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [identityColor, setIdentityColor] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [location, setLocation] = useState('');

  // Agenda state
  const [agendaMode, setAgendaMode] = useState<'recurring' | 'custom'>('recurring');
  const [daySchedules, setDaySchedules] = useState<DaySchedule[]>([
    { day: 'mon', labelAr: 'الاثنين', labelEn: 'Mon', selected: false, startTime: '', endTime: '' },
    { day: 'tue', labelAr: 'الثلاثاء', labelEn: 'Tue', selected: false, startTime: '', endTime: '' },
    { day: 'wed', labelAr: 'الأربعاء', labelEn: 'Wed', selected: false, startTime: '', endTime: '' },
    { day: 'thu', labelAr: 'الخميس', labelEn: 'Thu', selected: false, startTime: '', endTime: '' },
    { day: 'fri', labelAr: 'الجمعة', labelEn: 'Fri', selected: false, startTime: '', endTime: '' },
    { day: 'sat', labelAr: 'السبت', labelEn: 'Sat', selected: false, startTime: '', endTime: '' },
    { day: 'sun', labelAr: 'الأحد', labelEn: 'Sun', selected: false, startTime: '', endTime: '' },
  ]);

  // Custom dates state
  const [customDates, setCustomDates] = useState<CustomDateEntry[]>([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date(2026, 4, 1)); // May 2026

  // Participants state
  const [selectedTeachers, setSelectedTeachers] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);

  const [saving, setSaving] = useState(false);

  // Specialization date boundaries — projects must fall within these
  const [specStartDate, setSpecStartDate] = useState('');
  const [specEndDate, setSpecEndDate] = useState('');

  // Track existing projects' total weight for validation
  const [usedWeight, setUsedWeight] = useState(0);
  useEffect(() => {
    const fetchSpecAndWeights = async () => {
      // Fetch specialization dates
      if (specializationId) {
        try {
          const spec = await talentSpecializationsApi.get(specializationId);
          if (spec?.start_date) setSpecStartDate(spec.start_date);
          if (spec?.end_date) setSpecEndDate(spec.end_date);
        } catch (e) {
          console.log('[AddProject] Failed to fetch specialization dates:', e);
        }
      }
      // Fetch existing project weights
      try {
        const resp = await talentProjectsApi.list({ specialization: specializationId });
        const projects = resp?.results || resp;
        if (Array.isArray(projects)) {
          const total = projects
            .filter((p: any) => p.id !== projectId) // exclude current project in edit mode
            .reduce((sum: number, p: any) => sum + (parseFloat(p.weight) || 0), 0);
          setUsedWeight(total);
        }
      } catch (e) {
        console.log('[AddProject] Failed to fetch existing weights:', e);
      }
    };
    fetchSpecAndWeights();
  }, [specializationId, projectId]);

  const remainingWeight = Math.max(0, 100 - usedWeight);
  const currentWeight = parseFloat(weight) || 0;
  const weightExceeds = currentWeight > remainingWeight;

  // KPIs state — empty default; edit mode fills via useEffect
  const [kpis, setKpis] = useState<KPIFormData[]>([]);

  // Edit mode: fetch project data from API
  useEffect(() => {
    if (!projectId) return;
    const fetchProject = async () => {
      try {
        const project = await talentProjectsApi.get(projectId);
        if (project) {
          setName(project.name || '');
          setWeight(String(project.weight || ''));
          setDescription(project.description || '');
          setStartDate(project.start_date || '');
          setEndDate(project.end_date || '');
          setIdentityColor(project.identity_color || '');
          setLocation(project.location || '');
          // Pre-fill KPIs from kpi_links if available
          if (project.kpi_links?.length) {
            setKpis(project.kpi_links.map((link: any) => ({
              id: `existing_${link.id}`,
              kpiId: link.kpi,
              name: link.kpi_name || '',
              weight: String(link.kpi_weight ?? ''),
              description: link.kpi_description || '',
              kpiType: link.kpi_type || '',
              polarity: link.kpi_polarity || '',
              unit: link.kpi_unit || '',
              frequency: link.kpi_frequency || '',
              formula: link.kpi_formula || '',
              targetValue: String(link.target_value ?? link.kpi_target_value ?? ''),
              dataSource: link.kpi_data_source || '',
              goalWeight: '',
              startDate: link.kpi_measurement_start_date || '',
              isGeneral: !!link.kpi_is_general,
              isExpanded: false,
            })));
          }
          // Pre-fill teachers if available
          if (project.teachers?.length) {
            setSelectedTeachers(project.teachers.map((t: any) => t.name));
          }
        }
      } catch (e) {
        console.log('[AddProject] Failed to fetch project for edit:', e);
      }
    };
    fetchProject();
  }, [projectId]);
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);

  // KPI Options
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Generate unique ID
  const generateId = () => `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Add new KPI
  const addKpi = () => {
    const newKpi: KPIFormData = {
      id: generateId(),
      name: '',
      weight: '',
      description: '',
      kpiType: '',
      polarity: '',
      unit: '',
      frequency: '',
      formula: '',
      targetValue: '',
      dataSource: '',
      goalWeight: '',
      startDate: '',
      isGeneral: false,
      isExpanded: true,
    };
    setKpis([...kpis, newKpi]);
  };

  /** Weight left for one KPI card — 100% minus what the sibling cards claim. */
  const remainingKpiWeight = (kpiId: string) => {
    const others = kpis
      .filter(k => k.id !== kpiId)
      .reduce((sum, k) => sum + (parseFloat(k.weight) || 0), 0);
    return Math.max(0, 100 - others);
  };

  // Update KPI
  const updateKpi = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, [field]: value } : kpi)));
  };

  // Remove KPI
  const removeKpi = (id: string) => {
    setKpis(kpis.filter(kpi => kpi.id !== id));
  };

  // Toggle KPI expansion
  const toggleKpiExpansion = (id: string) => {
    setKpis(kpis.map(kpi => (kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi)));
  };

  const toggleDay = (dayKey: string) => {
    setDaySchedules(daySchedules.map(d =>
      d.day === dayKey ? { ...d, selected: !d.selected } : d
    ));
  };

  const updateDayTime = (dayKey: string, field: 'startTime' | 'endTime', value: string) => {
    setDaySchedules(daySchedules.map(d =>
      d.day === dayKey ? { ...d, [field]: value } : d
    ));
  };

  // Custom date helpers
  const toggleCustomDate = (dateStr: string) => {
    if (customDates.find(d => d.date === dateStr)) {
      setCustomDates(customDates.filter(d => d.date !== dateStr));
    } else {
      setCustomDates([...customDates, { date: dateStr, startTime: '09:00', endTime: '10:00' }]);
    }
  };

  const updateCustomDateTime = (dateStr: string, field: 'startTime' | 'endTime', value: string) => {
    setCustomDates(customDates.map(d => d.date === dateStr ? { ...d, [field]: value } : d));
  };

  const removeCustomDate = (dateStr: string) => {
    setCustomDates(customDates.filter(d => d.date !== dateStr));
  };

  const formatDateArabic = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const monthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
    return `${dayNames[date.getDay()]} - ${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatDateEnglish = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${dayNames[date.getDay()]} - ${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];

    // Previous month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrevMonth - i;
      const m = month === 0 ? 11 : month - 1;
      const y = month === 0 ? year - 1 : year;
      cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
    }

    // Next month leading days to fill grid
    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      const m = month === 11 ? 0 : month + 1;
      const y = month === 11 ? year + 1 : year;
      cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
    }

    return cells;
  };

  const arabicMonthNames = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  const englishMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Validate form
  const [dateError, setDateError] = useState('');

  const validateForm = () => {
    setDateError('');
    if (!name.trim()) return false;
    if (!description.trim()) return false;
    if (!startDate) return false;
    if (!endDate) return false;
    // Validate end date is after start date
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end <= start) {
      setDateError(isRTL
        ? 'تاريخ نهاية المشروع يجب أن يكون بعد تاريخ البداية'
        : 'End date must be after start date');
      return false;
    }
    // Validate project dates within specialization date range
    if (specStartDate && new Date(startDate) < new Date(specStartDate)) {
      setDateError(isRTL
        ? `تاريخ بداية المشروع يجب أن يكون بعد تاريخ بداية الموهبة (${specStartDate})`
        : `Project start date must be on or after talent start date (${specStartDate})`);
      return false;
    }
    if (specEndDate && new Date(endDate) > new Date(specEndDate)) {
      setDateError(isRTL
        ? `تاريخ نهاية المشروع يجب أن يكون قبل تاريخ نهاية الموهبة (${specEndDate})`
        : `Project end date must be on or before talent end date (${specEndDate})`);
      return false;
    }
    return true;
  };

  // Save project
  const handleSave = async () => {
    if (!validateForm()) return;
    if (weightExceeds) return;

    try {
      setSaving(true);

      const projectData: any = {
        name: name.trim(),
        description: description.trim(),
        weight: parseFloat(weight) || 0,
        start_date: startDate,
        end_date: endDate,
        specialization: specializationId,
        kpis: kpis.filter(kpi => kpi.name.trim()).map(kpi => ({
          ...(kpi.kpiId ? { kpi_id: kpi.kpiId } : {}),
          name: kpi.name.trim(),
          description: kpi.description.trim(),
          weight: parseFloat(kpi.weight) || 0,
          kpi_type: kpi.kpiType || 'strategic',
          polarity: kpi.polarity || 'positive',
          unit: kpi.unit || '%',
          frequency: kpi.frequency || 'monthly',
          formula: kpi.formula.trim(),
          target_value: parseFloat(kpi.targetValue) || 0,
          data_source: kpi.dataSource.trim(),
          goal_weight: parseFloat(kpi.goalWeight) || 0,
          measurement_start_date: kpi.startDate || null,
          is_general: !!kpi.isGeneral,
        })),
      };

      if (isEditMode) {
        await talentProjectsApi.update(projectId, projectData);
      } else {
        await talentProjectsApi.create(projectData);
      }
      navigation.goBack();
    } catch (error: any) {
      console.error('[AddProject] Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[styles.content, { padding: containerPadding }]}
      >
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            ...(isEditMode
              ? [{ label: isRTL ? 'تفاصيل المشروع' : 'Project Details', route: 'ProjectDetail' as const }]
              : [{ label: t('specializationDetail.title'), route: 'SpecializationDetail' as const }]
            ),
            { label: isEditMode ? (isRTL ? 'تعديل المشروع' : 'Edit Project') : t('addProject.title') },
          ]}
        />

        {/* Main Card */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {isEditMode ? (isRTL ? 'تعديل المشروع' : 'Edit Project') : t('addProject.title')}
          </Text>

          {/* ============================================ */}
          {/* Section 1: Basic Information */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {t('addProject.basicInfo')}
            </Text>

            {/* Row 1: Name + Weight */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {t('addProject.weightFromSpecialization')}
                  </Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity
                    style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}
                    onPress={() => setWeight(String(remainingWeight))}
                  >
                    <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                  </TouchableOpacity>
                  <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: weightExceeds ? '#DC2626' : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={weight}
                    onChangeText={(v) => setWeight(normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                <Text style={[styles.weightHint, { color: weightExceeds ? '#DC2626' : themeColors.placeholder }]}>
                  {isRTL
                    ? `المتبقي من وزن الموهبة: %${remainingWeight}${usedWeight > 0 ? ` (مستخدم: %${usedWeight})` : ''}`
                    : `Remaining: ${remainingWeight}%${usedWeight > 0 ? ` (Used: ${usedWeight}%)` : ''}`}
                </Text>
                {weightExceeds && (
                  <Text style={styles.weightError}>
                    {isRTL
                      ? 'مجموع أوزان المشاريع لا يمكن أن يتجاوز %100'
                      : 'Total project weights cannot exceed 100%'}
                  </Text>
                )}
              </View>

              {/* Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('common.name')}</Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={name}
                  onChangeText={setName}
                  placeholder={t('addProject.namePlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 2: Description */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.description')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={description}
                onChangeText={setDescription}
                placeholder={isRTL ? 'أدخل وصف المشروع' : 'Enter project description'}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Row 3: Start Date + End Date */}
            <View style={[styles.row, isMobile && styles.rowMobile]}>
              {/* End Date */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('addProject.endDate')}</Text>
                </View>
                <SingleDatePicker
                  value={endDate}
                  onChange={(val) => {
                    setEndDate(val);
                    if (startDate && val && new Date(val) <= new Date(startDate)) {
                      setDateError(isRTL ? 'تاريخ نهاية المشروع يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date');
                    } else {
                      setDateError('');
                    }
                  }}
                  minDate={startDate || specStartDate || undefined}
                  maxDate={specEndDate || undefined}
                  placeholder={isRTL ? 'اختر تاريخ النهاية' : 'Select end date'}
                />
                {dateError ? (
                  <Text style={styles.dateError}>{dateError}</Text>
                ) : null}
              </View>

              {/* Start Date */}
              <View style={[styles.field, { flex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('addProject.startDate')}</Text>
                </View>
                <SingleDatePicker
                  value={startDate}
                  onChange={(val) => {
                    setStartDate(val);
                    if (endDate && val && new Date(endDate) <= new Date(val)) {
                      setDateError(isRTL ? 'تاريخ نهاية المشروع يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date');
                    } else {
                      setDateError('');
                    }
                  }}
                  minDate={specStartDate || undefined}
                  maxDate={endDate || specEndDate || undefined}
                  placeholder={isRTL ? 'اختر تاريخ البداية' : 'Select start date'}
                />
              </View>
            </View>

            {/* Date range hint from specialization */}
            {specStartDate && specEndDate && (
              <Text style={{ fontSize: 12, fontFamily: 'Cairo-Regular', color: '#64748B', textAlign: 'right', marginTop: -4 }}>
                {isRTL
                  ? `الفترة المسموحة للمشروع: ${specStartDate} إلى ${specEndDate}`
                  : `Allowed project dates: ${specStartDate} to ${specEndDate}`}
              </Text>
            )}

            {/* Row 4 removed — Identity Color + Location belong to Activity screen */}
          </View>


          {/* ============================================ */}
          {/* Section 2: KPIs */}
          {/* ============================================ */}
          <View style={[styles.kpiSection, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            {/* Section Header */}
            <TouchableOpacity
              style={styles.kpiSectionHeader}
              onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
            >
              {kpiSectionExpanded ? (
                <ChevronUpIcon size={20} color={themeColors.iconColor} />
              ) : (
                <ChevronDownIcon size={20} color={themeColors.iconColor} />
              )}
              <Text style={[styles.kpiSectionTitle, { color: themeColors.sectionTitle }]}>
                {t('addProject.kpiSection')}
              </Text>
            </TouchableOpacity>

            {kpiSectionExpanded && (
              <View style={styles.kpiSectionContent}>
                {/* KPI Cards */}
                {kpis.map((kpi, index) => (
                  <View key={kpi.id} style={[styles.kpiCard, { backgroundColor: themeColors.kpiCard, borderColor: themeColors.kpiCardBorder }]}>
                    {/* KPI Header */}
                    <View style={[styles.kpiCardHeader, { backgroundColor: themeColors.kpiCardHeader, borderBottomColor: themeColors.kpiCardBorder }]}>
                      <View style={styles.kpiCardHeaderLeft}>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => toggleKpiExpansion(kpi.id)}
                        >
                          {kpi.isExpanded ? (
                            <ChevronUpIcon size={20} color={themeColors.iconColor} />
                          ) : (
                            <ChevronDownIcon size={20} color={themeColors.iconColor} />
                          )}
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={styles.kpiHeaderButton}
                          onPress={() => removeKpi(kpi.id)}
                        >
                          <TrashIcon size={18} color={themeColors.iconColor} />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.kpiCardHeaderRight}>
                        <Text style={[styles.kpiCardName, { color: themeColors.inputText }]}>
                          {kpi.name || `${t('kpis.kpiName')} ${index + 1}`}
                        </Text>
                        <View style={[styles.kpiCardNumber, { backgroundColor: themeColors.kpiNumber }]}>
                          <Text style={[styles.kpiCardNumberText, { color: themeColors.inputText }]}>{index + 1}</Text>
                        </View>
                      </View>
                    </View>

                    {/* KPI Content - Expanded */}
                    {kpi.isExpanded && (
                      <View style={styles.kpiCardContent}>
                        {/* Basic Information Subsection */}
                        <View style={[styles.subsection, { backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>
                            {t('kpis.basicInformation')}
                          </Text>

                          {/* Name & Weight Row */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Weight */}
                            <View style={styles.fieldSmall}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('common.weight')}</Text>
                              </View>
                              <View style={styles.weightInputRow}>
                                <TouchableOpacity
                                  style={[styles.setWeightButton, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}
                                  onPress={() => updateKpi(kpi.id, 'weight', String(remainingKpiWeight(kpi.id)))}
                                >
                                  <Text style={[styles.setWeightButtonText, { color: themeColors.inputText }]}>{t('kpis.setWeight')}</Text>
                                </TouchableOpacity>
                                <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                                <TextInput
                                  style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.weight}
                                  onChangeText={(v) => updateKpi(kpi.id, 'weight', normalizeArabicNumbers(v))}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>

                            {/* Name */}
                            <View style={styles.fieldLarge}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiName')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.name}
                                onChangeText={(v) => updateKpi(kpi.id, 'name', v)}
                                placeholder={isRTL ? 'أدخل اسم المؤشر' : 'Enter KPI name'}
                                textAlign="right"
                              />
                            </View>
                          </View>

                          {/* Description */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.description')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.description}
                              onChangeText={(v) => updateKpi(kpi.id, 'description', v)}
                              placeholder={isRTL ? 'أدخل وصف المؤشر' : 'Enter KPI description'}
                              textAlign="right"
                              multiline
                              numberOfLines={3}
                            />
                          </View>

                          {/* Overall / general KPI — readings are taken per
                              student enrolled in the project and averaged. */}
                          <View style={styles.kpiToggleRow}>
                            <View style={styles.kpiToggleTextWrap}>
                              <Text style={[styles.label, { color: themeColors.label }]}>
                                {isRTL ? 'مؤشر عام' : 'Overall KPI'}
                              </Text>
                              <Text style={[styles.kpiToggleDescription, { color: themeColors.placeholder }]}>
                                {isRTL
                                  ? 'مؤشر عام (يتم تطبيق المؤشر على جميع الطلاب المندرجين تحت المشروع، يحسب الإنجاز بشكل إفرادي لكل طالب ويظهر المتوسط العام للإنجاز)'
                                  : 'Overall KPI (applied to every student enrolled in the project. Achievement is calculated per student and the average is displayed.)'}
                              </Text>
                            </View>
                            <TouchableOpacity
                              activeOpacity={0.8}
                              onPress={() => updateKpi(kpi.id, 'isGeneral', !kpi.isGeneral)}
                              style={[styles.kpiToggleTrack, { backgroundColor: kpi.isGeneral ? '#A9211F' : '#D1D5DB' }]}
                            >
                              <View style={[
                                styles.kpiToggleThumb,
                                kpi.isGeneral ? styles.kpiToggleThumbOn : styles.kpiToggleThumbOff,
                              ]} />
                            </TouchableOpacity>
                          </View>
                        </View>

                        {/* Classification Subsection */}
                        <View style={[styles.subsection, { zIndex: 30, backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>
                            {t('kpis.classification')}
                          </Text>

                          <View style={[styles.row, isMobile && styles.rowMobile, { zIndex: 10 }]}>
                            {/* Polarity */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.polarity')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.polarity}
                                options={polarityOptions}
                                placeholder={t('kpis.selectPolarityHint')}
                                onChange={(v) => updateKpi(kpi.id, 'polarity', v)}
                              />
                            </View>

                            {/* KPI Type */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.kpiClassification')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.kpiType}
                                options={kpiTypeOptions}
                                placeholder={t('kpis.selectClassification')}
                                onChange={(v) => updateKpi(kpi.id, 'kpiType', v)}
                              />
                            </View>
                          </View>
                        </View>

                        {/* Measurement Subsection */}
                        <View style={[styles.subsection, { zIndex: 20, backgroundColor: themeColors.section }]}>
                          <Text style={[styles.subsectionTitle, { color: themeColors.sectionTitle }]}>
                            {t('kpis.measurement')}
                          </Text>

                          {/* Row: Goal Weight + Measurement Start Date */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Measurement Start Date */}
                            <View style={[styles.field, { flex: 1 }]}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementStartDate')}</Text>
                              </View>
                              <View style={[styles.dateInputContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]}>
                                {Platform.OS === 'web' ? (
                                  <input
                                    type="date"
                                    value={kpi.startDate}
                                    onChange={(e) => updateKpi(kpi.id, 'startDate', e.target.value)}
                                    style={{
                                      flex: 1,
                                      border: 'none',
                                      outline: 'none',
                                      backgroundColor: 'transparent',
                                      fontSize: 14,
                                      fontFamily: 'inherit',
                                      color: themeColors.inputText,
                                      textAlign: 'right',
                                      direction: 'rtl',
                                      padding: '10px 0',
                                      cursor: 'pointer',
                                    }}
                                  />
                                ) : (
                                  <>
                                    <CalendarIcon size={16} color={themeColors.iconColor} />
                                    <TextInput
                                      style={[styles.dateInput, { color: themeColors.inputText }]}
                                      value={kpi.startDate}
                                      onChangeText={(v) => updateKpi(kpi.id, 'startDate', v)}
                                      placeholder="DD-MM-YYYY"
                                      placeholderTextColor={themeColors.placeholder}
                                      textAlign="right"
                                    />
                                  </>
                                )}
                              </View>
                            </View>

                            {/* Goal Weight */}
                            <View style={[styles.field, { flex: 1 }]}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('addProject.kpiGoalWeight')}</Text>
                              </View>
                              <View style={styles.weightInputRow}>
                                <Text style={[styles.percentSymbol, { color: themeColors.iconColor }]}>%</Text>
                                <TextInput
                                  style={[styles.weightInput, { flex: 1, backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                  value={kpi.goalWeight}
                                  onChangeText={(v) => updateKpi(kpi.id, 'goalWeight', normalizeArabicNumbers(v))}
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                            </View>
                          </View>

                          {/* Row: Unit + Frequency */}
                          <View style={[styles.row, isMobile && styles.rowMobile, { zIndex: 10 }]}>
                            {/* Frequency */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementFrequency')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.frequency}
                                options={frequencyOptions}
                                placeholder={t('kpis.selectFrequency')}
                                onChange={(v) => updateKpi(kpi.id, 'frequency', v)}
                              />
                            </View>

                            {/* Unit */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.measurementUnit')}</Text>
                              </View>
                              <Dropdown
                                value={kpi.unit}
                                options={unitOptions}
                                placeholder={t('kpis.selectUnit')}
                                onChange={(v) => updateKpi(kpi.id, 'unit', v)}
                              />
                            </View>
                          </View>

                          {/* Formula */}
                          <View style={styles.field}>
                            <View style={styles.labelRow}>
                              <Text style={styles.requiredStar}>*</Text>
                              <Text style={[styles.label, { color: themeColors.label }]}>{t('addProject.measurementFormula')}</Text>
                            </View>
                            <TextInput
                              style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                              value={kpi.formula}
                              onChangeText={(v) => updateKpi(kpi.id, 'formula', v)}
                              placeholder={isRTL ? 'الصيغة الرياضية أو طريقة احتساب المؤشر' : 'Formula or how the KPI is calculated'}
                              textAlign="right"
                              multiline
                              numberOfLines={3}
                            />
                          </View>

                          {/* Row: Target Value + Data Source */}
                          <View style={[styles.row, isMobile && styles.rowMobile]}>
                            {/* Data Source */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.dataSource')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.dataSource}
                                onChangeText={(v) => updateKpi(kpi.id, 'dataSource', v)}
                                placeholder={isRTL ? 'مصدر بيانات المؤشر (تقرير، نظام، قسم)' : 'KPI data source (report, system, department)'}
                                textAlign="right"
                              />
                            </View>

                            {/* Target Value */}
                            <View style={styles.fieldHalf}>
                              <View style={styles.labelRow}>
                                <Text style={styles.requiredStar}>*</Text>
                                <Text style={[styles.label, { color: themeColors.label }]}>{t('kpis.targetValue')}</Text>
                              </View>
                              <TextInput
                                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                                value={kpi.targetValue}
                                onChangeText={(v) => updateKpi(kpi.id, 'targetValue', normalizeArabicNumbers(v))}
                                keyboardType="numeric"
                                placeholder={isRTL ? '0.0' : '0.0'}
                                textAlign="right"
                              />
                            </View>
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                ))}

                {/* Add KPI Button */}
                <TouchableOpacity style={[styles.addKpiButton, { borderColor: themeColors.inputBorder }]} onPress={addKpi}>
                  <Text style={[styles.addKpiButtonText, { color: themeColors.inputText }]}>{t('kpis.addPerformanceIndicator')}</Text>
                  <PlusIcon size={16} color={themeColors.inputText} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>{t('common.save')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
            onPress={() => navigation.goBack()}
            disabled={saving}
          >
            <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    paddingBottom: 48,
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    gap: 20,
  },
  pageTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'right',
  },
  // Section
  section: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 8,
  },
  // Form Fields
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  rowMobile: {
    flexDirection: 'column',
  },
  field: {
    gap: 6,
  },
  fieldLarge: {
    flex: 2,
    gap: 6,
  },
  fieldSmall: {
    flex: 1,
    gap: 6,
  },
  fieldHalf: {
    flex: 1,
    gap: 6,
  },
  labelRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#D62728',
    fontFamily: FONTS.regular,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 36,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.02,
    shadowRadius: 1,
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  // Overall (general) KPI toggle
  kpiToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    marginTop: 12,
  },
  kpiToggleTextWrap: { flex: 1, gap: 4 },
  kpiToggleDescription: { fontSize: 12, fontFamily: FONTS.regular, lineHeight: 18 },
  kpiToggleTrack: {
    width: 44,
    height: 24,
    borderRadius: 12,
    padding: 2,
    justifyContent: 'center',
  },
  kpiToggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
  },
  kpiToggleThumbOn: { alignSelf: 'flex-end' },
  kpiToggleThumbOff: { alignSelf: 'flex-start' },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  weightInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    flex: 1,
    textAlign: 'center',
  },
  weightHint: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    marginTop: 4,
  },
  weightError: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#DC2626',
    textAlign: 'right',
    marginTop: 2,
  },
  dateError: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#DC2626',
    textAlign: 'right',
    marginTop: 4,
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
    ...(Platform.OS === 'web' ? { overflow: 'visible' as any } : {}),
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Dropdown
  dropdownContainer: {
    position: 'relative',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 36,
  },
  dropdownDisabled: {
    opacity: 0.6,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Multi-select
  multiSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    minHeight: 36,
    gap: 8,
  },
  tagsWrap: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    justifyContent: 'flex-end',
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 4,
  },
  tagText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  tagX: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '600',
  },
  // Color row
  colorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    justifyContent: 'flex-end',
  },
  colorSwatch: {
    width: 24,
    height: 24,
    borderRadius: 4,
  },
  colorPalette: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    paddingTop: 8,
  },
  colorOption: {
    width: 32,
    height: 32,
    borderRadius: 6,
  },
  colorOptionSelected: {
    borderWidth: 3,
    borderColor: '#171717',
  },
  // Agenda
  agendaToggle: {
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  agendaToggleBtn: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  agendaToggleBtnActive: {
    backgroundColor: '#A6813F',
  },
  agendaToggleBtnInactive: {
    backgroundColor: '#FFFFFF',
  },
  agendaToggleText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  agendaToggleTextActive: {
    color: '#FFFFFF',
  },
  agendaToggleTextInactive: {
    color: '#414042',
  },
  dayPillsRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  dayPill: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#FFFFFF',
  },
  dayPillSelected: {
    backgroundColor: '#414042',
    borderColor: '#414042',
  },
  dayPillText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  dayPillTextSelected: {
    color: '#FFFFFF',
  },
  scheduleRow: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-end',
  },
  dayLabel: {
    backgroundColor: '#414042',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dayLabelText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#FFFFFF',
  },
  // Time Input
  timeInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  // Custom Dates Calendar
  customDateCard: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  customDateCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  customDateTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    textAlign: 'right',
  },
  customDateRemoveBtn: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarContainer: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 16,
  },
  calendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  calendarMonthText: {
    fontSize: 16,
    fontFamily: FONTS.bold,
  },
  calendarDayHeaders: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  calendarDayHeaderText: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    fontFamily: FONTS.medium,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  calendarCell: {
    width: '14.28%' as any,
    aspectRatio: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarCellSelected: {
    backgroundColor: '#64748B',
    borderRadius: 20,
  },
  calendarCellToday: {
    backgroundColor: '#E2E8F0',
    borderRadius: 20,
  },
  calendarCellText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  calendarCellTextSelected: {
    color: '#FFFFFF',
  },
  // KPI Section
  kpiSection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    gap: 8,
    minHeight: 44,
  },
  kpiSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'center',
  },
  kpiSectionContent: {
    padding: 16,
    paddingTop: 0,
    gap: 12,
  },
  // KPI Card
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  kpiCardHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  kpiHeaderButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardNumber: {
    width: 28,
    height: 28,
    borderRadius: 6,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiCardNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  kpiCardName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  kpiCardContent: {
    padding: 16,
    gap: 16,
  },
  // Subsection
  subsection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    gap: 16,
  },
  subsectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
    marginBottom: 8,
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    borderStyle: 'dashed',
    backgroundColor: '#FFFFFF',
    minHeight: 44,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
